import { Fragment, useEffect, useState } from 'react';
import type { PlatformTenantSummary } from '@stonelink/shared';
import { api } from '../api';
import { Badge, Button, Card, ErrorNote, PageHeader, formatDate } from '../ui';

const numberFormat = new Intl.NumberFormat();

/**
 * Stonelink's own view across every tenant.
 *
 * Deliberately marked out from the rest of the console — an accent rail and a
 * "Platform" eyebrow — because the whole point of every other page is that it
 * only ever shows you one tenant.
 */
export function PlatformAdminPage() {
  const [tenants, setTenants] = useState<PlatformTenantSummary[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [members, setMembers] = useState<
    Record<string, { id: string; email: string; role: string }[]>
  >({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => setTenants(await api.listPlatformTenants());

  useEffect(() => {
    void load().catch(() => setError('Could not load the tenant list.'));
  }, []);

  const toggle = async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!members[id]) {
      const detail = await api.getPlatformTenant(id);
      setMembers((prev) => ({ ...prev, [id]: detail.members }));
    }
  };

  const setStatus = async (tenant: PlatformTenantSummary) => {
    const next = tenant.status === 'active' ? 'disabled' : 'active';
    if (
      next === 'disabled' &&
      !confirm(
        `Suspend ${tenant.name}?\n\nTheir team is locked out of the console and the chat widget stops answering on their site immediately.`,
      )
    ) {
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.setPlatformTenantStatus(tenant.id, next);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not change that tenant.');
    } finally {
      setBusy(false);
    }
  };

  if (!tenants) {
    return (
      <div>
        <PageHeader title="Platform" description="Every tenant on Stonelink Assist." />
        {error ? <ErrorNote>{error}</ErrorNote> : <p className="text-sm text-muted">Loading…</p>}
      </div>
    );
  }

  const active = tenants.filter((tenant) => tenant.status === 'active').length;

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="Platform"
        description={`Every tenant on Stonelink Assist — ${active} active of ${tenants.length}. This is the only page that looks across workspaces.`}
      />

      {error && <ErrorNote>{error}</ErrorNote>}

      <Card title="Tenants" description="Token totals cover the last 30 days.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left font-mono text-[10.5px] tracking-[0.12em] text-muted uppercase">
              <tr>
                <th className="pb-3 pr-4 font-medium">Workspace</th>
                <th className="pb-3 pr-4 font-medium">Status</th>
                <th className="pb-3 pr-4 font-medium">Team</th>
                <th className="pb-3 pr-4 font-medium">Facts</th>
                <th className="pb-3 pr-4 font-medium">Today</th>
                <th className="pb-3 pr-4 font-medium">Tokens 30d</th>
                <th className="pb-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <Fragment key={tenant.id}>
                  <tr className="border-t border-line align-top">
                    <td className="py-3 pr-4">
                      <button
                        type="button"
                        onClick={() => void toggle(tenant.id)}
                        className="text-left transition hover:text-brand"
                      >
                        <span className="font-medium">{tenant.name}</span>
                        <span className="block font-mono text-xs text-muted">{tenant.slug}</span>
                      </button>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge tone={tenant.status === 'active' ? 'good' : 'bad'}>
                        {tenant.status}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 text-muted">{tenant.memberCount}</td>
                    <td className="py-3 pr-4 text-muted">{tenant.factCount}</td>
                    <td className="py-3 pr-4 text-muted">{tenant.messagesToday}</td>
                    <td className="py-3 pr-4 whitespace-nowrap text-muted">
                      {numberFormat.format(tenant.inputTokens30d + tenant.outputTokens30d)}
                    </td>
                    <td className="py-3">
                      <Button
                        variant={tenant.status === 'active' ? 'danger' : 'secondary'}
                        disabled={busy}
                        onClick={() => void setStatus(tenant)}
                      >
                        {tenant.status === 'active' ? 'Suspend' : 'Reactivate'}
                      </Button>
                    </td>
                  </tr>
                  {expanded === tenant.id && (
                    <tr className="border-t border-line/50">
                      <td colSpan={7} className="py-3 pl-1">
                        <div className="rounded-xl border-l-2 border-brand/40 bg-white/4 py-3 pl-4">
                          <p className="font-mono text-[10.5px] tracking-[0.12em] text-muted uppercase">
                            Members · joined {formatDate(tenant.createdAt)}
                          </p>
                          <ul className="mt-2 space-y-1">
                            {(members[tenant.id] ?? []).map((member) => (
                              <li key={member.id} className="flex items-center gap-2 text-sm">
                                <span>{member.email}</span>
                                <Badge tone={member.role === 'owner' ? 'brand' : 'neutral'}>
                                  {member.role}
                                </Badge>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
